import { SelectOption } from '../../../../../../../tailwindComponents/selectTW/interfaces';
import { useMemo } from 'react';
import { useSelectCurrentOrder } from '../../../../../../../selectors/order/selectCurrentOrder';
import { UUID } from '../../../../../../../types/shared/UUID';
import { eDepositorType } from 'types/order/emdAccess/eDepositorType';
import { ePartyType } from '../../../../../../../ui-toolkit/enums/ePartyType';
import {
  IPartySignatory,
  IPartySignatoryDataWithIdAndEmail,
  isSignatoryWithIdAndEmail,
} from '../../../../../../../ui-toolkit/orders/interfaces/IPartySignatory';
import {
  IPartyPerson,
  IPartyPersonWithEmail,
  isPartyPersonWithEmail,
} from '../../../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { isBuyerBorrower } from '../../../../../../../ui-toolkit/orders/interfaces/IParty';

type ValidDepositor = IPartyPersonWithEmail | IPartySignatoryDataWithIdAndEmail;

// A depositor is the Beth who will pay the EMD. A depositor can be either a person or a signatory.
// This hook returns the list of possible Beths of an order. There is rarely more than one.
// The Beth must have an email.
// A map of a depositor's id to their type is returned. The request body requires a depositorType
export const useGetDepositorOptions = () => {
  const { data } = useSelectCurrentOrder();

  const depositors: ValidDepositor[] = useMemo(() => {
    return data.parties
      .filter(isBuyerBorrower)
      .flatMap<IPartyPerson | IPartySignatory, 1>((buyerBorrower) => {
        if (buyerBorrower.type === ePartyType.person) {
          return buyerBorrower;
        }
        return buyerBorrower.signatories;
      })
      .filter(isValidDepositor);
  }, [data]);

  const depositorOptions: SelectOption[] = useMemo(
    () => depositors.map((buyerSigner) => ({ label: getNameAndEmail(buyerSigner), value: buyerSigner.id })),
    [depositors]
  );

  const depositorTypesDictionary: Map<UUID, eDepositorType> = useMemo(() => {
    const map = new Map<UUID, eDepositorType>();
    depositors.forEach((depositor) => {
      const depositorType = getDepositorType(depositor);
      map.set(depositor.id, depositorType);
    });
    return map;
  }, [depositors]);

  return { depositorOptions, depositorTypesDictionary };
};

const isPersonDepositor = (contact: ValidDepositor): contact is IPartyPersonWithEmail => {
  return 'type' in contact && contact.type === ePartyType.person;
};

const getNameAndEmail = (contact: ValidDepositor): string => {
  if (isPersonDepositor(contact)) {
    return `${contact.first_name} ${contact.last_name} - ${contact.email}`;
  }
  return `${contact.firstName} ${contact.lastName} - ${contact.email}`;
};

const getDepositorType = (depositor: ValidDepositor): eDepositorType => {
  if (isPersonDepositor(depositor)) {
    return eDepositorType.Person;
  }
  return eDepositorType.Signatory;
};

const isPartyPerson = (contact: IPartyPerson | IPartySignatory): contact is IPartyPerson => {
  return 'type' in contact && contact.type === ePartyType.person;
};

const isValidDepositor = (potentialDepositor: IPartyPerson | IPartySignatory): potentialDepositor is ValidDepositor => {
  if (isPartyPerson(potentialDepositor)) {
    return isPartyPersonWithEmail(potentialDepositor);
  }
  return isSignatoryWithIdAndEmail(potentialDepositor);
};
